<template>
  <div
    :data-testid="sectionTag"
    class="wasabi__loader"
    :role="decorative ? undefined : 'status'"
    :aria-label="decorative ? undefined : resolvedLabel"
    :aria-hidden="decorative ? 'true' : undefined"
  >
    <div class="dot" />
    <div class="dot" />
    <div class="dot" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { $t } from '@/plugins/i18n'
import { tokens } from '@/plugins/vuetify'
import { SHARED_FEATURES_SELECTORS } from '@/constants/selectors/shared-features.selectors'

export interface WLoaderProps {
  sectionTag?: string
  color?: 'light' | 'dark'
  label?: string
  decorative?: boolean
}

const props = withDefaults(defineProps<WLoaderProps>(), {
  sectionTag: SHARED_FEATURES_SELECTORS.WASABI_LOADER,
  color: 'dark',
  label: '',
  decorative: false,
})

const resolvedLabel = computed(() => props.label.trim() || $t('app.common.loading.label'))
const dotColor = computed(() =>
  props.color === 'dark' ? tokens.color.progress.indicator : tokens.color.content.text.inverse
)
</script>

<style scoped>
.wasabi__loader {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 20px;
  line-height: 1;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background-color: v-bind('dotColor');
  opacity: 0.72;
  transform: translateY(0) scale(0.96);
  transform-origin: center center;
  animation: wloader-soft-float 1.45s infinite;
}

.dot:nth-child(2) {
  animation-delay: 0.16s;
}

.dot:nth-child(3) {
  animation-delay: 0.32s;
}

@keyframes wloader-soft-float {
  0% {
    transform: translateY(0) scale(0.96);
    opacity: 0.72;
    animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  }

  25% {
    transform: translateY(-4px) scale(1.02);
    opacity: 1;
    animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
  }

  50% {
    transform: translateY(0) scale(0.98);
    opacity: 0.84;
    animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  }

  75% {
    transform: translateY(4px) scale(0.94);
    opacity: 0.64;
    animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
  }

  100% {
    transform: translateY(0) scale(0.96);
    opacity: 0.72;
  }
}

@media (prefers-reduced-motion: no-preference) {
  .dot {
    will-change: transform, opacity;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dot {
    animation: none;
    opacity: 0.85;
    transform: none;
  }
}
</style>
